import { z } from "zod";
import {
  CORPORATE_NUMBER_FORMAT_ERROR_MESSAGE,
  isValidCorporateNumberInput,
} from "../utils/storedPictureSearch";

/** 各条件の最大文字数（BE `StoredCommercialPictureSearchRequestSerializer` の max_length と同じ） */
export const COMPANY_NAME_MAX_LENGTH = 256;
export const CORPORATE_NUMBER_MAX_LENGTH = 256;
export const CITY_MAX_LENGTH = 128;
export const ADDRESS_REST_MAX_LENGTH = 512;

/** 取得日の形式エラーメッセージ */
export const ACQUIRED_DATE_FORMAT_ERROR_MESSAGE =
  "取得日の形式が正しくありません";

/** 取得日の範囲エラーメッセージ */
export const ACQUIRED_DATE_RANGE_ERROR_MESSAGE =
  "取得日の開始日は終了日以前の日付を指定してください";

/** 検索ボタンの横に出す案内。条件なしで検索すると、取得ユーザーの範囲の全件を表示する */
export const SEARCH_CONDITION_HINT_MESSAGE =
  "複数条件はAND検索。条件なしで検索すると全件を表示します";

/** 文字列条件の一致方式 */
export const MATCH_TYPES = ["partial", "exact", "prefix", "suffix"] as const;
export type MatchType = (typeof MATCH_TYPES)[number];

/** 一致方式セレクトの選択肢 */
export const MATCH_TYPE_OPTIONS: Array<{ value: MatchType; label: string }> = [
  { value: "partial", label: "部分一致" },
  { value: "exact", label: "完全一致" },
  { value: "prefix", label: "前方一致" },
  { value: "suffix", label: "後方一致" },
];

const matchTypeSchema = z.enum(MATCH_TYPES);

const acquiredDateSchema = z
  .date({ errorMap: () => ({ message: ACQUIRED_DATE_FORMAT_ERROR_MESSAGE }) })
  .nullable();

/**
 * 取得済商業登記 検索フォームのスキーマ。
 * 各条件は任意だが、少なくとも1つは指定が必要（すべて未指定＝全件返却はBE/FEとも拒否する）。
 */
export const storedPictureSearchConditionSchema = z
  .object({
    companyName: z
      .string()
      .max(
        COMPANY_NAME_MAX_LENGTH,
        `法人名は${COMPANY_NAME_MAX_LENGTH}文字以内で入力してください`
      ),
    corporateNumber: z
      .string()
      .max(
        CORPORATE_NUMBER_MAX_LENGTH,
        `会社法人等番号は${CORPORATE_NUMBER_MAX_LENGTH}文字以内で入力してください`
      )
      .refine(isValidCorporateNumberInput, {
        message: CORPORATE_NUMBER_FORMAT_ERROR_MESSAGE,
      }),
    companyNameMatchType: matchTypeSchema,
    // 都道府県・市区町村・取得状況は複数選択（同一項目内はOR）
    prefectures: z.array(z.string()),
    cities: z.array(z.string()),
    addressRest: z
      .string()
      .max(
        ADDRESS_REST_MAX_LENGTH,
        `それ以降の住所は${ADDRESS_REST_MAX_LENGTH}文字以内で入力してください`
      ),
    statuses: z.array(z.string()),
    acquiredDateRange: z.tuple([acquiredDateSchema, acquiredDateSchema]),
  })
  .refine(
    ({ acquiredDateRange: [acquiredDateFrom, acquiredDateTo] }) =>
      acquiredDateFrom === null ||
      acquiredDateTo === null ||
      acquiredDateFrom <= acquiredDateTo,
    { message: ACQUIRED_DATE_RANGE_ERROR_MESSAGE, path: ["acquiredDateRange"] }
  );

export type StoredPictureSearchFormData = z.infer<
  typeof storedPictureSearchConditionSchema
>;

/** 検索フォームの初期値（すべて未指定） */
export const storedPictureSearchDefaultValues: StoredPictureSearchFormData = {
  companyName: "",
  corporateNumber: "",
  companyNameMatchType: "partial",
  prefectures: [],
  cities: [],
  addressRest: "",
  statuses: [],
  acquiredDateRange: [null, null],
};

/**
 * 取得状況（明細status）の選択肢。value は BE の status 値と一致させる。
 * ラベルは一覧（取得依頼から探す）の取得状況列の表記に合わせる（取得中→依頼中／失敗→失敗あり）。
 */
export const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "SUCCEEDED", label: "成功" },
  { value: "REQUESTING", label: "依頼中" },
  { value: "FAILED", label: "失敗あり" },
];
